import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { Plus, Edit, Trash2, Search } from 'lucide-react';
import DataTable from '../common/DataTable';
import Modal from '../common/Modal';
import apiService from '../../services/api';

const SEOManagement = ({ user }) => {
  const [seoSettings, setSeoSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSEO, setEditingSEO] = useState(null);
  const [formData, setFormData] = useState({
    page_name: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    og_title: '',
    og_description: '',
    og_image: '',
    canonical_url: '',
    status: 'active'
  });

  useEffect(() => {
    fetchSEOSettings();
  }, []);

  const fetchSEOSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        apiService.setToken(token);
      }
      const data = await apiService.getSEOSettings();
      setSeoSettings(data);
    } catch (error) {
      toast.error('Failed to fetch SEO settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (token) {
        apiService.setToken(token);
      }

      if (editingSEO) {
        await apiService.updateSEOSetting(editingSEO.id, formData);
      } else {
        await apiService.createSEOSetting(formData);
      }

      toast.success(`SEO setting ${editingSEO ? 'updated' : 'created'} successfully`);
      setShowModal(false);
      setEditingSEO(null);
      setFormData({ page_name: '', meta_title: '', meta_description: '', meta_keywords: '', og_title: '', og_description: '', og_image: '', canonical_url: '', status: 'active' });
      fetchSEOSettings();
    } catch (error) {
      toast.error('Operation failed');
    }
  };

  const handleEdit = (seo) => {
    setEditingSEO(seo);
    setFormData(seo);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    toast((t) => (
      <div>
        <p>Are you sure you want to delete this SEO setting?</p>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
          <button
            onClick={async () => {
              toast.dismiss(t.id);
              try {
                const token = localStorage.getItem('token');
                if (token) {
                  apiService.setToken(token);
                }
                await apiService.deleteSEOSetting(id);
                toast.success('SEO setting deleted successfully');
                fetchSEOSettings();
              } catch (error) {
                toast.error('Failed to delete SEO setting');
              }
            }}
            style={{ background: '#ef4444', color: 'white', border: 'none', padding: '0.25rem 0.5rem', borderRadius: '4px' }}
          >
            Delete
          </button>
          <button
            onClick={() => toast.dismiss(t.id)}
            style={{ background: '#6b7280', color: 'white', border: 'none', padding: '0.25rem 0.5rem', borderRadius: '4px' }}
          >
            Cancel
          </button>
        </div>
      </div>
    ), { duration: Infinity });
  };

  const columns = [
    { key: 'page_name', label: 'Page Name' },
    { key: 'meta_title', label: 'Meta Title' },
    {
      key: 'meta_description',
      label: 'Meta Description',
      render: (seo) => seo.meta_description?.substring(0, 50) + '...'
    },
    { key: 'status', label: 'Status' },
    {
      key: 'actions',
      label: 'Actions',
      render: (seo) => (
        <div className="action-buttons">
          <button onClick={() => handleEdit(seo)} className="btn-edit">
            <Edit size={16} />
          </button>
          {user?.role === 'super_admin' && (
            <button onClick={() => handleDelete(seo.id)} className="btn-delete">
              <Trash2 size={16} />
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="seo-management">
      <div className="page-header">
        <h2><Search size={24} /> SEO Management</h2>
        <motion.button
          className="btn-primary"
          onClick={() => setShowModal(true)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Plus size={16} /> Add SEO Setting
        </motion.button>
      </div>

      <DataTable
        data={seoSettings}
        columns={columns}
        loading={loading}
        emptyMessage="No SEO settings found"
      />

      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingSEO(null);
          setFormData({ page_name: '', meta_title: '', meta_description: '', meta_keywords: '', og_title: '', og_description: '', og_image: '', canonical_url: '', status: 'active' });
        }}
        title={`${editingSEO ? 'Edit' : 'Add'} SEO Setting`}
      >
        <form onSubmit={handleSubmit} style={{ textAlign: 'left' }}>
          <div className="form-group">
            <label>Page Name</label>
            <input
              type="text"
              required
              value={formData.page_name}
              onChange={(e) => setFormData({ ...formData, page_name: e.target.value })}
              placeholder="e.g., Home, About, Contact"
            />
          </div>
          <div className="form-group">
            <label>Meta Title</label>
            <input
              type="text"
              required
              value={formData.meta_title}
              onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
              placeholder="SEO optimized page title"
            />
          </div>
          <div className="form-group">
            <label>Meta Description</label>
            <textarea
              required
              value={formData.meta_description}
              onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
              rows="3"
              placeholder="Brief description for search engines (150-160 characters)"
            />
          </div>
          <div className="form-group">
            <label>Meta Keywords</label>
            <input
              type="text"
              value={formData.meta_keywords}
              onChange={(e) => setFormData({ ...formData, meta_keywords: e.target.value })}
              placeholder="keyword1, keyword2, keyword3"
            />
          </div>
          <div className="form-group">
            <label>Open Graph Title</label>
            <input
              type="text"
              value={formData.og_title}
              onChange={(e) => setFormData({ ...formData, og_title: e.target.value })}
              placeholder="Title for social media sharing"
            />
          </div>
          <div className="form-group">
            <label>Open Graph Description</label>
            <textarea
              value={formData.og_description}
              onChange={(e) => setFormData({ ...formData, og_description: e.target.value })}
              rows="2"
              placeholder="Description for social media sharing"
            />
          </div>
          <div className="form-group">
            <label>Open Graph Image URL</label>
            <input
              type="url"
              value={formData.og_image}
              onChange={(e) => setFormData({ ...formData, og_image: e.target.value })}
              placeholder="Image URL for social media sharing"
            />
          </div>
          <div className="form-group">
            <label>Canonical URL</label>
            <input
              type="url"
              value={formData.canonical_url}
              onChange={(e) => setFormData({ ...formData, canonical_url: e.target.value })}
              placeholder="Canonical URL for this page"
            />
          </div>
          <div className="form-group">
            <label>Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setShowModal(false);
                setEditingSEO(null);
                setFormData({ page_name: '', meta_title: '', meta_description: '', meta_keywords: '', og_title: '', og_description: '', og_image: '', canonical_url: '', status: 'active' });
              }}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {editingSEO ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default SEOManagement;